import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnInit } from '@angular/core';
import { TestHeaderElementsService } from '../../services/test-header-elements.service';
import { IDisplayCondition, IDisplayElement, IDisplayField, IDisplaySource, IDisplayValue, IViewTemplate }
  from '../../interfaces/test-header-elements.interface';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { Observable, EMPTY, combineLatest } from 'rxjs';
import { TestActionsService } from '../../services/test-actions.service';
import * as immutable from 'immutable';
import { DiagnosticOrderActionsService } from '@ispm/test/services/diagnostic-order-actions.service';
import { TestRunningMode } from '@ispm/test/interfaces/test-running-mode.enum';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { get } from 'lodash';
import { NavigationConfigService } from '@ispm/navigation/services/navigationConfig.service';
import { INavigationConfigurationInterface } from '@ispm/navigation/interfaces';
import { TestService } from '../../services/test.service';
import { PatientService } from '@ispm-client/modules/patient/services/patient.service';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { distinctUntilChanged } from "rxjs/operators";
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { of } from 'rxjs/observable/of';

interface IDisplayHeaderField extends IDisplayField {
  displayed$: Observable<boolean>;
}

interface IDisplayHeaderElement extends IDisplayElement {
  fields: IDisplayHeaderField[];
}

@Component({
  selector: 'gen-test-header-elements',
  templateUrl: './test-header-elements.component.html',
  styleUrls: [ './test-header-elements.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestHeaderElementsComponent implements OnInit {

  @Input() patientId: string;
  @Input() showPatientWarning: boolean = false;
  @Input() backUrl: string;
  @Input() backUrlParams: object;
  @Input() set headerFieldsView(fieldsView: string[]) {
    this.headerFieldsView$.next(fieldsView);
  }
  @HostBinding('class.empty-elements') isEmpty: boolean = false;

  private headerFieldsView$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(null);

  public displayHeaderElements$: Observable<IDisplayHeaderElement[]>;
  public orderId$: Observable<string>;
  public elementTypes = ElementTypes;
  private defaultHeaderFields$: Observable<string[]>;

  constructor(
    private testService: TestService,
    private testHeaderElementsService: TestHeaderElementsService,
    private interpolateService: InterpolateService,
    private testActionService: TestActionsService,
    private changeDetector: ChangeDetectorRef,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private navigationConfigService: NavigationConfigService,
    private patientService: PatientService,
    private _store: Store<IgenomicsState>,
  ) { }

  ngOnInit() {
    const diagnosticOrder$ = this.diagnosticOrderActionsService.getState<IDiagnosticOrder>('order').pipe(
      distinctUntilChanged(), // TODO: remove?
      filter(order => !!order),
      catchError(error => {
        console.error('Error in diagnosticOrder$:', error);
        return EMPTY;
      }),
    );

    this.orderId$ = diagnosticOrder$.pipe(
      map(order => order.id),
      tap(() => this.changeDetector.markForCheck()),
      catchError(error => {
        console.error('Error in orderId$:', error);
        return EMPTY;
      }),
    );

    this.displayHeaderElements$ = diagnosticOrder$.pipe(
      switchMap(order => this.getHeaderElements(order.runningMode)),
      map(headerElements => this.extendToDisplayHeaderElements(headerElements)),
      tap(displayHeaderElements => this.isEmpty = !displayHeaderElements.length),
      tap(() => this.changeDetector.markForCheck()),
      catchError(error => {
        console.error('Error in displayElements$:', error);
        return EMPTY;
      }),
    );

    this.defaultHeaderFields$ = this.navigationConfigService.getWebConfiguration().pipe(
      distinctUntilChanged(), // TODO: remove?
      map((orgWebConfig: INavigationConfigurationInterface) =>
        [get(orgWebConfig?.navigation, 'patientHeaderView', 'defaultView')]
      ),
      tap(() => this.changeDetector.markForCheck()),
      catchError(error => {
        console.error('Error in defaultHeaderFields$:', error);
        return EMPTY;
      }),
    );
  }

  getDisplayedValue(value: IDisplayValue, field: IDisplayField): Observable<string> {
    const applyViewTransform = (elementValue: any, viewTemplate: IViewTemplate) => {
      if (elementValue) {
        return this.testHeaderElementsService.getTemplateInstance(viewTemplate.name)
          .transform(elementValue, ...viewTemplate.parameters);
      }
    };

    return this.getValueForElement(value).pipe(
      map(elementValue => value.viewTemplate && applyViewTransform(elementValue, value.viewTemplate) || elementValue),
      map(elementValue => field.viewTemplate && applyViewTransform(elementValue, field.viewTemplate) || elementValue),
    );
  }

  displayTooltipValues(): Observable<string> {
    return this._store.select('users', 'currentPatientIdentifiers').pipe(
      map(identifierData => this.patientService.getIdentifierToolTip(identifierData)),
      map( identifierToolTip => identifierToolTip.htmlContentIcon)
    );
  }

  getValueForElement(value: IDisplaySource, key?: string | number): Observable<any> {
    const theKey = typeof key !== 'undefined' ? key : value.keys.staticValue;
    const interpolatedStatement = this.interpolateService.interpolate(value.field, {
      patientId: this.patientId
    });

    switch (value.source) {
      case SourceTypes.STORE:
        return this.getValueFromStore(value.sourceKey, interpolatedStatement + '.' + theKey);
      default:
        return Observable.of(theKey);
    }
  }

  checkDisplayCondition(conditions: Array<IDisplayCondition>): Observable<boolean> {
    if (!conditions) {
      return Observable.of(true);
    }

    const checkCondition = (condition: IDisplayCondition) => {
      const sourceValue$ = this.getValueForElement(condition.source);
      const destinationValue$ = condition.destination.value !== undefined
          ? Observable.of(condition.destination.value)
          : this.getValueForElement(condition.destination);

      return combineLatest([sourceValue$, destinationValue$]).pipe(
        map(([sourceValue, destinationValue]) => {
          switch (condition.operation) {
            case ConditionOperationTypes.EQUALS:
              return sourceValue === destinationValue;
            case ConditionOperationTypes.NOT_EQUALS:
              return sourceValue !== destinationValue;
            case ConditionOperationTypes.EXISTS:
              return typeof sourceValue !== 'undefined' && sourceValue !== null;
            case ConditionOperationTypes.NOT_EXISTS:
              return typeof sourceValue === 'undefined' || sourceValue === null;
            case ConditionOperationTypes.PRIMARY_MRN:
              return sourceValue === "mrn";
            case ConditionOperationTypes.DEFAULT_MRN:
              return sourceValue !== "mrn";
            default:
              return false;
          }
        }),
      );
    };

    return combineLatest(conditions.map(condition => checkCondition(condition))).pipe(
      map(results => results.reduce((accum, result) => accum && result, true)),
      tap(() => this.changeDetector.markForCheck()),
    );
  }

  trackByFn(index, item) {
    return item ? item.id : index;
  }

  private getHeaderElements(testRunningMode?: TestRunningMode): Observable<IDisplayElement[]> {
    return this.headerFieldsView$.pipe(
      switchMap(headerFieldsView => {
        if (headerFieldsView) {
          return of(headerFieldsView);
        }

        if (this.testService.testConfiguration) {
          if (!!testRunningMode && this.testService.hasDynamicHeaderFields(testRunningMode)) {
            return of(this.testService.getDynamicHeaderFields(testRunningMode));
          }

          if (this.testService.testConfiguration.dynamicHeaderFields) {
            return of(this.testService.testConfiguration.dynamicHeaderFields);
          }
        }
        return this.defaultHeaderFields$;
      }),
      switchMap(headerFields => this.testHeaderElementsService.getHeaderElements(headerFields)),
    );
  }

  private extendToDisplayHeaderElements(headerElements: IDisplayElement[]): IDisplayHeaderElement[] {
    const extendField = (field: IDisplayField): IDisplayHeaderField => {
      const displayed$ = this.checkDisplayCondition(field.displayConditions);
      return {
        ...field,
        displayed$,
        values: field.values.map(value => ({
          ...value,
          displayedValue$: displayed$.pipe(
            filter(displayed => displayed),
            switchMap(() => this.getDisplayedValue(value, field)),
          )
        })),
      };
    };

    return headerElements.map(headerElement => ({
      ...headerElement,
      fields: headerElement.fields.map(extendField),
    }));
  }

  // watches sourceKey path in the store and returns keyPath value in Observable
  private getValueFromStore<T = any>(sourceKey: string, keyPath: string): Observable<T | null> {
    const splitPath = keyPath.split('.');
    if (!splitPath[0].length) {
      return Observable.of(null);
    }
    const getNestedState = (state: any, prop: string) => {
      if (state instanceof immutable.Map || state instanceof immutable.List) {
        return (state as any).get(prop) || null;
      }
      return state?.[prop] || null;
    };

    return this.testActionService.getStateByPath(sourceKey.split('.')).pipe(
      map(state => splitPath.reduce((accum, segment) => getNestedState(accum, segment), state)),
      catchError(err => {
        console.error(err);
        return Observable.of(null);
      })
    );
  }

  public checkMultiMRN() {
    return this._store.select('users', 'currentPatientIdentifiers').pipe(map(identifierData => {
      return identifierData?.MRN.contains(",") || identifierData?.EnterpriseID?.length > 0;
    }));
  }
}

export const SourceTypes = {
  STORE: 'store'
};

export const ElementTypes = {
  USER_PROFILE: 'user-profile',
  INDICATION: 'diagnosisForInterpretation',
  TEXT: 'text',
  POPUP: 'popup',
  ICON: 'icon',
  ONCOLOGY_REPORT: 'oncology_report'
};

export const ConditionOperationTypes = {
  NOT_EQUALS: 'not_equals',
  EQUALS: 'equals',
  EXISTS: 'exists',
  NOT_EXISTS: 'not_exists',
  PRIMARY_MRN: 'primary_mrn',
  DEFAULT_MRN: 'default_mrn'
};
